import { useEffect, useMemo, useState } from 'react'
import {
  AVATAR_GRID_COLUMNS,
  DEFAULT_AVATAR_EMOJI,
  MAX_DISPLAY_NAME_LENGTH,
  sanitizeDisplayName,
} from '../config/profile.ts'
import {
  ymgGetAvatarIcons,
  ymgSyncProfile,
  type LocalProfile,
} from '../services/hubProfile.ts'
import { t } from '../i18n/translations.ts'

interface Props {
  open: boolean
  currentProfile: LocalProfile | null
  onSaved: (profile: LocalProfile) => void
  onClose: () => void
}

type IconLoadState =
  | { status: 'loading' }
  | { status: 'ready'; icons: AvatarIcon[] }
  | { status: 'error'; message: string }

/**
 * プロフィール編集モーダル。
 * - 表示名（最大 MAX_DISPLAY_NAME_LENGTH 文字、制御文字は除去）
 * - アバターアイコン選択（GET /avatar-icons、セッションキャッシュ）
 *
 * 設計メモ:
 *  - HelpModal と同じオーバーレイ構造 / 閉じる挙動（背景タップ / ESC / ×）を採用。
 *  - バリデーション失敗 / 保存失敗時はモーダル内にメッセージを表示し、再試行可能。
 *  - LINE プロフィール取り込みは Phase 4（認証実装後）で追加予定のためここでは未対応。
 *  - Capacitor ネイティブでのローカルファースト保存は platform.ts 導入後（T07）に拡張。
 */
export function ProfileEditModal({
  open,
  currentProfile,
  onSaved,
  onClose,
}: Props) {
  const [displayName, setDisplayName] = useState<string>(
    currentProfile?.displayName ?? '',
  )
  const [selectedIconId, setSelectedIconId] = useState<string | null>(
    currentProfile?.avatarIconId ?? null,
  )
  const [iconState, setIconState] = useState<IconLoadState>({
    status: 'loading',
  })
  const [saving, setSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // open 切り替えや currentProfile 変化に合わせて入力値を初期化
  useEffect(() => {
    if (!open) return
    setDisplayName(currentProfile?.displayName ?? '')
    setSelectedIconId(currentProfile?.avatarIconId ?? null)
    setErrorMessage(null)
    setSaving(false)
  }, [open, currentProfile])

  // ESC で閉じる（保存中は無効）
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, saving, onClose])

  // 背景スクロール抑止
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  // アイコン一覧ロード（open になったとき / まだキャッシュが無いとき）
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setIconState({ status: 'loading' })
    ymgGetAvatarIcons()
      .then((icons) => {
        if (cancelled) return
        setIconState({ status: 'ready', icons })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        console.warn('[ProfileEdit] failed to load avatar icons:', err)
        setIconState({
          status: 'error',
          message: t('iconLoadFailed'),
        })
      })
    return () => {
      cancelled = true
    }
  }, [open])

  const sanitized = useMemo(() => sanitizeDisplayName(displayName), [displayName])
  const canSave =
    !saving &&
    sanitized.length > 0 &&
    sanitized.length <= MAX_DISPLAY_NAME_LENGTH

  if (!open) return null

  const selectedIcon =
    iconState.status === 'ready'
      ? iconState.icons.find((ic) => ic.id === selectedIconId) ?? null
      : null

  const handleSave = async () => {
    if (!canSave) return
    setSaving(true)
    setErrorMessage(null)
    try {
      const next = await ymgSyncProfile({
        displayName: sanitized,
        avatarIconId: selectedIconId,
      })
      onSaved(next)
      onClose()
    } catch (err) {
      console.warn('[ProfileEdit] save failed:', err)
      setErrorMessage(t('saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const handleOverlayClick = () => {
    if (saving) return
    onClose()
  }

  return (
    <div
      className="profile-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="profile-modal-title"
      onClick={handleOverlayClick}
    >
      <div
        className="profile-modal__dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="profile-modal__header">
          <h2 id="profile-modal-title" className="profile-modal__title">
            {t('profileEdit')}
          </h2>
          <button
            type="button"
            className="profile-modal__close"
            aria-label={t('closeAria')}
            onClick={onClose}
            disabled={saving}
          >
            ×
          </button>
        </header>

        <div className="profile-modal__body" style={{ touchAction: 'pan-y' }}>
          {/* プレビュー */}
          <div className="profile-modal__preview">
            <div className="profile-modal__avatar">
              {selectedIcon ? (
                <img
                  src={selectedIcon.imageUrl}
                  alt={selectedIcon.name.ja || selectedIcon.name.en}
                />
              ) : (
                <span className="profile-modal__avatar-fallback">
                  {DEFAULT_AVATAR_EMOJI}
                </span>
              )}
            </div>
            <div className="profile-modal__preview-name">
              {sanitized || t('player')}
            </div>
          </div>

          {/* 表示名入力 */}
          <section className="profile-modal__section">
            <label className="profile-modal__label" htmlFor="profile-name">
              {t('displayName')}
            </label>
            <input
              id="profile-name"
              type="text"
              className="profile-modal__input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={MAX_DISPLAY_NAME_LENGTH * 2 /* 入力段階では少し余裕 */}
              placeholder={t('displayNamePlaceholder')}
              disabled={saving}
              autoComplete="off"
            />
            <div className="profile-modal__counter">
              {sanitized.length}/{MAX_DISPLAY_NAME_LENGTH}
            </div>
          </section>

          {/* アイコングリッド */}
          <section className="profile-modal__section">
            <div className="profile-modal__label">{t('chooseIcon')}</div>
            {iconState.status === 'loading' && (
              <div className="profile-modal__icons-placeholder">
                {t('loading')}
              </div>
            )}
            {iconState.status === 'error' && (
              <div className="profile-modal__icons-placeholder profile-modal__icons-placeholder--error">
                {iconState.message}
              </div>
            )}
            {iconState.status === 'ready' && iconState.icons.length === 0 && (
              <div className="profile-modal__icons-placeholder">
                {t('noIcons')}
              </div>
            )}
            {iconState.status === 'ready' && iconState.icons.length > 0 && (
              <div
                className="profile-modal__icons"
                style={{
                  // 列数は config 由来で動的なため inline で渡す。display:grid と gap は CSS 側。
                  gridTemplateColumns: `repeat(${AVATAR_GRID_COLUMNS}, 1fr)`,
                }}
              >
                {iconState.icons.map((icon) => {
                  const selected = icon.id === selectedIconId
                  return (
                    <button
                      key={icon.id}
                      type="button"
                      className={`profile-modal__icon-cell${
                        selected ? ' profile-modal__icon-cell--active' : ''
                      }`}
                      onClick={() => setSelectedIconId(icon.id)}
                      aria-label={icon.name.ja || icon.name.en}
                      disabled={saving}
                    >
                      <img
                        src={icon.imageUrl}
                        alt={icon.name.ja || icon.name.en}
                        loading="lazy"
                      />
                    </button>
                  )
                })}
              </div>
            )}
          </section>

          {errorMessage && (
            <div className="profile-modal__error" role="alert">
              {errorMessage}
            </div>
          )}
        </div>

        <footer className="profile-modal__footer">
          <button
            type="button"
            className="btn btn--secondary"
            onClick={onClose}
            disabled={saving}
          >
            {t('adCancel')}
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSave}
            disabled={!canSave}
          >
            {saving ? t('saving') : t('save')}
          </button>
        </footer>
      </div>
    </div>
  )
}
